/**
 * AUTENTICAÇÃO DO MAPA EXTERNO
 * Página de login com CPF para acesso ao mapa de distribuição externo
 */

import { useState } from 'react'

const MapaExternoAuth = () => {
  const [codigo, setCodigo] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleCodigoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Aceitar apenas números
    const value = e.target.value.replace(/\D/g, '')
    setCodigo(value)
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!codigo || codigo.length < 4) {
      setError('Código inválido')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetch('/api/public/mapa/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ codigo }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Código não autorizado')
      }

      // Salvar token de acesso no sessionStorage
      sessionStorage.setItem('mapaToken', data.token)
      sessionStorage.setItem('mapaUser', JSON.stringify(data.user))

      // Recarregar página para mostrar o mapa autenticado
      window.location.reload()
    } catch (err) {
      console.error('Erro ao autenticar:', err)
      setError(err instanceof Error ? err.message : 'Erro ao validar código')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen w-screen flex items-center justify-center bg-gradient-to-br from-[#00524E] via-[#007a73] to-[#00524E] relative overflow-hidden">
      {/* Background decorativo */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
      </div>

      <div className="w-full max-w-md p-6 relative z-10">
        {/* Card de Login */}
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-10 border border-white/20">
          {/* Logo */}
          <div className="text-center mb-10">
            <img
              src="/tw1.png"
              alt="Logo"
              className="h-20 w-auto mx-auto"
            />
          </div>
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Identifique-se
            </h2>
            <p className="text-sm text-gray-600">
              Digite seu código para acessar o mapa
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Campo Código */}
            <div>
              <label
                htmlFor="codigo"
                className="block text-sm font-semibold text-gray-700 mb-3"
              >
                Código
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="codigo"
                  value={codigo}
                  onChange={handleCodigoChange}
                  placeholder="Digite seu código"
                  maxLength={20}
                  className={`w-full pl-12 pr-4 py-4 border-2 rounded-xl focus:outline-none focus:ring-4 transition-all text-lg font-medium text-gray-800 placeholder:text-gray-400 ${
                    error
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100 bg-red-50'
                      : 'border-gray-200 focus:border-[#00524E] focus:ring-[#00524E]/10 bg-white'
                  }`}
                  disabled={loading}
                  autoFocus
                />
              </div>
              {error && (
                <div className="mt-3 flex items-start gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-100">
                  <svg
                    className="w-5 h-5 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">{error}</span>
                </div>
              )}
            </div>

            {/* Botão de Submit */}
            <button
              type="submit"
              disabled={loading || !codigo || codigo.length < 4}
              className="w-full bg-gradient-to-r from-[#00524E] to-[#007a73] hover:from-[#007a73] hover:to-[#00524E] text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin" />
                  <span className="text-lg">Verificando...</span>
                </>
              ) : (
                <>
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-lg">Acessar Mapa</span>
                </>
              )}
            </button>
          </form>

          {/* Informação adicional */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p>Apenas códigos cadastrados têm acesso ao sistema</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-white/70 text-sm">
          <p className="font-medium">Sistema de Mapa de Distribuição © 2026</p>
        </div>
      </div>
    </div>
  )
}

export default MapaExternoAuth
